import { useCallback, useId, useMemo, useState } from 'react';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import { Button, DialogContent } from '@mui/material';
import classNames from 'classnames/bind';
import { getApiErrorMessage } from '@api/getApiError';
import FormikModal from '@components/Modals/FormikModal';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import { useModalProvider } from '@components/Modals/ModalContext';
import { useAddVoyageMutation, useFetchVoyageQuery, useUpdateVoyageMutation } from '@api/Trinium/Endpoints/Vessels/slice';
import { FormikTextInput } from '@forms/Components/TextInput';
import { FormikDateTimePicker } from '@forms/Components/DateTimePicker';
import { FormikDatePicker } from '@forms/Components/DatePicker';
import { VesselAutocompleteField } from '@forms/Fields/VesselAutocomplete';
import LocationField from '@forms/Fields/Location';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';
import { parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { getStringOrBlank } from '@/Infrastructure/Utilities/string.type';
import { getVesselVoyageValidationSchema, VesselVoyageForm, VesselVoyageFormikModalProps, voyageFormToApiSchema } from './types';

import styles from './VesselModal.module.css';
const stylesContext = classNames.bind(styles);

/**
 * Combined Formik Modal for a Vessel and its Voyage.
 *
 * A voyage is only ever entered against a vessel, so the two share one modal. Once saved, everything
 * that identifies the voyage — vessel, terminal, voyage number — is locked and only the dates stay
 * editable; that is what stops one terminal from overwriting another terminal's details on the same
 * vessel. Anything else has to be corrected by duplicating the voyage into a new one.
 */
export default function VesselVoyageFormikModal({ vessel, voyageId, isExport, onSaved }: VesselVoyageFormikModalProps) {
  const { data: editVoyage, isLoading } = useFetchVoyageQuery(
    { vesselId: vessel?.id ?? '', voyageId: String(voyageId) },
    { skip: !voyageId || !vessel, refetchOnMountOrArgChange: true }
  );
  const { queueModalAnnouncement } = useModalProvider();
  const [isOpen, setIsOpen] = useState(true);
  const formId = useId();
  const [addVoyage] = useAddVoyageMutation();
  const [updateVoyage] = useUpdateVoyageMutation();

  /** Values carried over by the Duplicate action. Their presence turns the modal back into an add. */
  const [duplicatedValues, setDuplicatedValues] = useState<VesselVoyageForm | null>(null);

  const isEditMode = Boolean(voyageId) && !duplicatedValues;

  /** What an add starts from, and the placeholder an edit renders against until its voyage lands. */
  const blankValues: VesselVoyageForm = useMemo(
    () => ({
      vessel: vessel ?? null,
      terminal: null,
      voyageIn: '',
      etaDate: null,
      demurrageDate: null,
      voyageOut: '',
      frd: null,
      cutoffDate: null
    }),
    [vessel]
  );

  const initialValues: VesselVoyageForm | null = useMemo(() => {
    if (duplicatedValues) return duplicatedValues;

    // Adding: nothing to load, start from whatever vessel the caller already had
    if (!voyageId) return blankValues;

    if (isLoading || !editVoyage) return null;

    return {
      id: voyageId,
      vessel: vessel ?? null,
      terminal: editVoyage.terminal,
      voyageIn: getStringOrBlank(editVoyage.voyageIn),
      etaDate: parseDayJs(editVoyage.etaParsedDate?.unix),
      demurrageDate: parseDayJs(editVoyage.lastFreeParsedDate?.unix),
      voyageOut: getStringOrBlank(editVoyage.voyageOut),
      frd: parseDayJs(editVoyage.firstReceivedDate?.unix),
      cutoffDate: parseDayJs(editVoyage.cutoffParsedDate?.unix)
    };
  }, [blankValues, duplicatedValues, editVoyage, isLoading, vessel, voyageId]);

  const isLoadingVoyage = !initialValues;

  const validationSchema = useMemo(() => getVesselVoyageValidationSchema(isExport, isEditMode), [isEditMode, isExport]);

  const cardStyles = stylesContext({
    card: true,
    ['card-content']: true,
    ['flex-100']: true,
    fieldset: true
  });

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Everything carries over except the saved record's identity and its terminal — duplicating a
   * voyage exists precisely so it can be re-entered against a different terminal.
   */
  const onDuplicate = useCallback((values: VesselVoyageForm) => {
    setDuplicatedValues({ ...values, id: undefined, terminal: null });
  }, []);

  const handleSubmit = useCallback(
    async (values: VesselVoyageForm) => {
      const selectedVessel = values.vessel;
      if (!selectedVessel) return;

      const mutations = voyageFormToApiSchema(values, isExport);

      const apiPromise = values.id
        ? updateVoyage({
            vesselId: selectedVessel.id,
            voyage: { id: values.id, ...mutations }
          })
        : addVoyage({
            vesselId: selectedVessel.id,
            voyage: mutations
          });

      await apiPromise
        .unwrap()
        .then((savedVoyage) => {
          onSaved?.(selectedVessel, savedVoyage);
          closeModal();
        })
        .catch((error: unknown) => {
          const errorMessage = getApiErrorMessage(error);
          queueModalAnnouncement({
            severity: 'error',
            message: errorMessage
          });
        });
    },
    [addVoyage, closeModal, isExport, onSaved, queueModalAnnouncement, updateVoyage]
  );

  return (
    <FormikModal
      isOpen={isOpen}
      onClose={closeModal}
      paperClass={styles['modal']}>
      {/* The header owns the only way out of the modal, so it stays mounted while the voyage loads —
          otherwise a fetch that never resolves leaves nothing to close. */}
      <Formik
        // Formik only reads initial values on mount, so remount when loading finishes or on a duplicate
        key={duplicatedValues ? 'duplicate' : isLoadingVoyage ? 'loading' : 'original'}
        initialValues={initialValues ?? blankValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {(formikProps: FormikProps<VesselVoyageForm>) => (
          <>
            <FormikModalHeader
              title='Vessel & Voyage'
              formName={formId}
              onClose={closeModal}
              actions={isEditMode && !isLoadingVoyage ? <DuplicateVoyageButton onDuplicate={onDuplicate} /> : undefined}
            />

            <DialogContent>
              {isLoadingVoyage ? (
                <ApplicationSpinner height={300} />
              ) : (
                <Form
                  id={formId}
                  className={styles['form-col']}>
                  <fieldset className={cardStyles}>
                    <legend className={styles['legend']}>Vessel Details</legend>

                    <VesselAutocompleteField
                      fieldName='vessel'
                      label='Vessel (Name or Code)'
                      className={styles['full-width']}
                      disabled={isEditMode}
                      canAddVessel={!isEditMode}
                      required
                    />
                  </fieldset>

                  {/* A voyage belongs to a vessel, so there is nothing to enter until one is picked.
                      Native fieldset[disabled] cascades to every nested input and button. */}
                  <fieldset
                    className={`${cardStyles} ${styles['gated-fieldset']}`}
                    disabled={!formikProps.values.vessel}>
                    <legend className={styles['legend']}>Voyage Details</legend>

                    <div className={styles['flex-row']}>
                      <LocationField
                        fieldName='terminal'
                        label={isExport ? 'Departing Port Terminal' : 'Arriving Port Terminal'}
                        className={styles['voyage-field']}
                        disabled={isEditMode}
                        selectionOnly
                        required
                      />

                      <Field
                        name={isExport ? 'voyageOut' : 'voyageIn'}
                        component={FormikTextInput}
                        label={isExport ? 'Voyage Out' : 'Voyage In'}
                        className={styles['voyage-field']}
                        disabled={isEditMode}
                        required
                      />
                    </div>

                    <div className={styles['flex-row']}>
                      {isExport ? (
                        <>
                          <Field
                            name='frd'
                            component={FormikDatePicker}
                            label='First Return Date'
                            className={styles['voyage-field']}
                          />

                          <Field
                            name='cutoffDate'
                            component={FormikDateTimePicker}
                            label='Cut Off Date'
                            className={styles['voyage-field']}
                            required
                          />
                        </>
                      ) : (
                        <>
                          <Field
                            name='etaDate'
                            component={FormikDateTimePicker}
                            label='Estimated Time of Arrival'
                            className={styles['voyage-field']}
                          />

                          <Field
                            name='demurrageDate'
                            component={FormikDatePicker}
                            label='Demurrage LFD'
                            className={styles['voyage-field']}
                          />
                        </>
                      )}
                    </div>
                  </fieldset>
                </Form>
              )}
            </DialogContent>
          </>
        )}
      </Formik>
    </FormikModal>
  );
}

/**
 * Offered while viewing a saved voyage, where everything but the dates is locked. It reopens the same
 * modal as an add, prefilled from what is on screen, so a mistake can be re-entered instead of edited.
 */
function DuplicateVoyageButton(props: { onDuplicate: (values: VesselVoyageForm) => void }) {
  const { values } = useFormikContext<VesselVoyageForm>();

  return (
    <Button
      variant='contained'
      className={styles['duplicate-action']}
      onClick={() => {
        props.onDuplicate(values);
      }}>
      Duplicate
    </Button>
  );
}
